import React from 'react'
import TopNavigationBarContext from '../../contexts/top-navigation-bar'
import Link from '@mui/material/Link'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { SxProps, Theme, useTheme } from '@mui/material/styles'
import LinkOulined from '@mui/icons-material/LinkOutlined'

import AdminIcon from '../custom-icons/product-links/admin-icon'
import BimIcon from '../custom-icons/product-links/bim'
import CmsIcon from '../custom-icons/product-links/cms'
import DamIcon from '../custom-icons/product-links/dam'
import MailIcon from '../custom-icons/product-links/mail'
import PimIcon from '../custom-icons/product-links/pim'
import CsIcon from '../custom-icons/product-links/cs'
import { TopBarButton } from './components/button'
import { TopNavigationBar } from '../../api/settings'
import ECSearchIcon from '../custom-icons/product-links/ecsearch'

const Component: React.FC = React.memo(function Component() {
    const topNavigationBars = React.useContext(TopNavigationBarContext)
    const theme = useTheme()

    const isCurrentApp = (appName: string): boolean =>
        String(appName).toLowerCase() === 'users'

    const sortList = sortLinks(topNavigationBars)
    return (
        <Box
            data-testid='top-navigation-bar'
            display='flex'
            component='nav'
            justifyContent='center'
            gap={1}
        >
            {sortList.map((navigation) => (
                <Link
                    component={TopBarButton}
                    href={navigation.url}
                    key={navigation.name}
                    sx={
                        isCurrentApp(navigation.name)
                            ? linkActive(theme)
                            : linkInactive(theme)
                    }
                >
                    <LinkIcon linkName={navigation.name} />
                    <Typography
                        sx={{
                            fontWeight: 500,
                            fontSize: '12px',
                            lineHeight: '18px',
                            letterSpacing: '2px',
                            color: 'inherit',
                            display: isCurrentApp(navigation.name)
                                ? 'block'
                                : 'none',
                            [theme.breakpoints.up('md')]: {
                                display: 'block',
                            },
                        }}
                    >
                        {navigation.name}
                    </Typography>
                </Link>
            ))}
        </Box>
    )
})

export default Component

const LinkIcon: React.FC<{ linkName: string }> = React.memo(function LinkIcon({
    linkName,
}) {
    switch (linkName.toLowerCase()) {
        case 'admin':
            return <AdminIcon />
        case 'users':
            return <BimIcon />
        case 'cms':
            return <CmsIcon />
        case 'dam':
            return <DamIcon />
        case 'mail':
            return <MailIcon />
        case 'pim':
            return <PimIcon />
        case 'search':
            return <ECSearchIcon />
        case 'commercial sort':
        case 'sort':
            return <CsIcon />
        default:
            return <LinkOulined />
    }
})

export const sortLinks = (links: TopNavigationBar[]) => {
    const Order = [
        'ADMIN',
        'PIM',
        'CMS',
        'DAM',
        'USERS',
        'MAIL',
        'SORT',
        'SEARCH',
    ]

    return [...links]
        .map((l) =>
            l.name.toUpperCase() === 'COMMERCIAL SORT'
                ? { ...l, name: 'sort' }
                : l
        )
        .sort((a, b) => {
            const index1 = Order.indexOf(a.name.toUpperCase())
            const index2 = Order.indexOf(b.name.toUpperCase())

            if (index1 === -1) return 1
            if (index2 === -1) return -1

            return index1 - index2
        })
}

const linkInactive = (theme: Theme): SxProps<Theme> => ({
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
        '& svg path': {
            fill: 'white',
        },
    },
    '@media (min-width: 960px)': {
        paddingRight: theme.spacing(4),
    },
})

const linkActive = (theme: Theme): SxProps<Theme> => ({
    ...linkInactive(theme),
    backgroundColor: theme.palette.primary.light,
    color: 'white',
    '& svg path': {
        fill: 'white',
    },
    '&:hover': {
        textDecoration: 'none',
    },
})
